import { createContext, useContext } from 'react';
import { State as CobrandingState } from '../../reducers/cobranding-reducer';

import { ApplicationData, lookupApplication } from '../applications';
import { AidFrontendConfigCollection } from '../feature-flags/dynamic-config';
import { AidFrontendDynamicConfig } from '../../providers/DynamicConfigs';
import { checkFeatureGate } from '../feature-flags/feature-gate';
import { AidFrontendFeatureGate } from '../../providers/FeatureGates';

export enum SimpleCobranding {
  NO_COBRANDING = 'no-cobranding',
  BITBUCKET = 'bitbucket',
  COMPASS = 'compass',
  CONFLUENCE = 'confluence',
  COMMUNITY = 'community',
  JIRA = 'jira',
  OPSGENIE = 'opsgenie',
  STATUSPAGE = 'statuspage',
  TRELLO = 'trello',
  WAC = 'wac',
  ATLAS = 'atlas',
  LOOM = 'loom',
  JIRA_ALIGN = 'jira-align',
  ADMIN = 'admin',
  ROVO = 'rovo',
  PRT = 'prt',
  SAC = 'sac', // support.atlassian.com
  SUPPORT = 'support', // IDOASIS-350: Deprecate in favour of "sac"
}

export enum AdvancedCobranding {
  DEMO = 'demo',
  PRT_DEMO = 'prt-demo',
  ATLAS_66195 = 'atlas-66195',
  CFIND_2409 = 'cfind-2409',
  TWCG_1160_JIRA = 'twcg-1160-jira',
  TWCG_1160_CONFLUENCE = 'twcg-1160-confluence',
  JIRA_ATLAS_75650 = 'jira-atlas-75650',
  CONFLUENCE_ATLAS_75650 = 'confluence-atlas-75650',
  LOOM = 'default',
  ACCOUNT_LINKING = 'account-linking',
  ENTRY_AUTH = 'entry-auth',
  WAC_LOOM = 'loom-cobranding',
  WAC_JPD = 'jpd',
  WAC_JSM = 'jsm',
  SERVICE_COLLECTION = 'service-collection',
  TEAMWORK_COLLECTION = 'teamwork-collection',
  ISOLATED_CLOUD = 'ic',
}

export const isSimpleCobranding = (value: string): value is SimpleCobranding =>
  Object.values<string>(SimpleCobranding).includes(value);

export const isAdvancedCobranding = (value: string): value is AdvancedCobranding =>
  Object.values<string>(AdvancedCobranding).includes(value);

const getDisabledApplicationKeys = (dynamicConfigs: AidFrontendConfigCollection) => {
  const disabledApplicationKeys = dynamicConfigs.getStringList(
    AidFrontendDynamicConfig.DISABLED_COBRANDING_APPLICATION_KEYS
  );

  if ('value' in disabledApplicationKeys) {
    return disabledApplicationKeys.value;
  }

  return [];
};

const getApplicationKey = (applicationData: ApplicationData) => {
  return `${applicationData.application}${
    applicationData.experience ? `--${applicationData.experience}` : ''
  }`;
};

export function getCurrentCobranding(
  cobranding: CobrandingState,
  dynamicConfigs: AidFrontendConfigCollection
): SimpleCobranding | AdvancedCobranding {
  const applicationData = lookupApplication(cobranding);

  if (applicationData) {
    const disabledApplicationKeys = getDisabledApplicationKeys(dynamicConfigs);
    const applicationKey = getApplicationKey(applicationData);

    if (disabledApplicationKeys.includes(applicationKey)) {
      console.log(`Cobranding disabled for application: ${applicationKey}`);
      return SimpleCobranding.NO_COBRANDING;
    }

    switch (applicationData.application) {
      case 'admin':
        switch (applicationData.experience) {
          case 'jira-atlas-75650':
            return AdvancedCobranding.JIRA_ATLAS_75650;
          case 'confluence-atlas-75650':
            return AdvancedCobranding.CONFLUENCE_ATLAS_75650;
          default:
            return SimpleCobranding.NO_COBRANDING;
        }
      case 'atlas':
        return SimpleCobranding.ATLAS;
      case 'bitbucket':
        return SimpleCobranding.BITBUCKET;
      case 'community': {
        if (applicationData.experience === 'account-linking') {
          return AdvancedCobranding.ACCOUNT_LINKING;
        }
        if (applicationData.experience === 'entry-auth') {
          return AdvancedCobranding.ENTRY_AUTH;
        }
        return SimpleCobranding.COMMUNITY;
      }
      case 'compass':
        return SimpleCobranding.COMPASS;
      case 'confluence':
        switch (applicationData.experience) {
          case 'twcg-1160':
            if (checkFeatureGate(AidFrontendFeatureGate.TWCG_1160_CONTEXTUAL_BACKGROUNDS)) {
              return AdvancedCobranding.TWCG_1160_CONFLUENCE;
            }
            return SimpleCobranding.CONFLUENCE;
          case 'mobile-ios-ic':
          case 'mobile-android-ic':
            return AdvancedCobranding.ISOLATED_CLOUD;
          default:
            return SimpleCobranding.CONFLUENCE;
        }
      case 'jira':
        switch (applicationData.experience) {
          case 'twcg-1160':
            if (checkFeatureGate(AidFrontendFeatureGate.TWCG_1160_CONTEXTUAL_BACKGROUNDS)) {
              return AdvancedCobranding.TWCG_1160_JIRA;
            }
            return SimpleCobranding.JIRA;
          case 'mobile-ios-ic':
          case 'mobile-android-ic':
            return AdvancedCobranding.ISOLATED_CLOUD;
          default:
            return SimpleCobranding.JIRA;
        }
      case 'loom':
        switch (applicationData.experience) {
          case 'default':
            return AdvancedCobranding.LOOM;
          default:
            return SimpleCobranding.LOOM;
        }
      case 'jira-align':
        return SimpleCobranding.JIRA_ALIGN;
      case 'opsgenie':
        return SimpleCobranding.OPSGENIE;
      case 'rovo':
        return SimpleCobranding.ROVO;
      case 'statuspage':
        return SimpleCobranding.STATUSPAGE;
      case 'trello':
        return SimpleCobranding.TRELLO;
      case 'wac':
        switch (applicationData.experience) {
          case 'otp-jira':
          case 'atlas-66195':
            return AdvancedCobranding.ATLAS_66195;
          case 'otp-confluence':
          case 'cfind-2409':
            return AdvancedCobranding.CFIND_2409;
          case 'loom':
            return AdvancedCobranding.WAC_LOOM;
          case 'jpd':
            return AdvancedCobranding.WAC_JPD;
          case 'jsm':
            return AdvancedCobranding.WAC_JSM;
          case 'service-collection':
            return AdvancedCobranding.SERVICE_COLLECTION;
          case 'teamwork-collection':
            return AdvancedCobranding.TEAMWORK_COLLECTION;
          default:
            return SimpleCobranding.NO_COBRANDING;
        }
      case 'support':
        switch (applicationData.experience) {
          case 'ic':
            return AdvancedCobranding.ISOLATED_CLOUD;
          default:
            return SimpleCobranding.NO_COBRANDING;
        }
      case 'sac':
        switch (applicationData.experience) {
          case 'ic':
            return AdvancedCobranding.ISOLATED_CLOUD;
          default:
            return SimpleCobranding.NO_COBRANDING;
        }
      case 'pollinator':
        if (applicationData.experience) {
          if (applicationData.experience.startsWith('jira')) {
            return SimpleCobranding.JIRA;
          }
          if (applicationData.experience.startsWith('confluence')) {
            return SimpleCobranding.CONFLUENCE;
          }
          if (applicationData.experience === 'demo') {
            return AdvancedCobranding.DEMO;
          }
        }
        return SimpleCobranding.NO_COBRANDING;
      case 'prt':
        if (applicationData.experience === 'demo') {
          return AdvancedCobranding.PRT_DEMO;
        }
        return SimpleCobranding.PRT;
      case 'mpac':
        if (applicationData.experience === 'ic') {
          return AdvancedCobranding.ISOLATED_CLOUD;
        }
        return SimpleCobranding.NO_COBRANDING;
    }
  }

  return SimpleCobranding.NO_COBRANDING;
}

export interface CobrandingContext {
  cobranding: SimpleCobranding | AdvancedCobranding;
  isMobileApp: boolean;
}

const CobrandingContext = createContext<CobrandingContext>({
  cobranding: SimpleCobranding.NO_COBRANDING,
  isMobileApp: false,
});

export const CobrandingProvider = CobrandingContext.Provider;
export const CobrandingConsumer = CobrandingContext.Consumer;

export const useCobranding = () => useContext<CobrandingContext>(CobrandingContext);
